import React, {memo, useState} from "react"
import Grid from "@mui/material/Grid";
import {useTranslation} from "react-i18next";
import FormSection from "../../../components/FormSection";
import {
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormGroup,
} from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import Model1MeasureField from "./Model1MeasureField";
import {formikPropertyEqual} from "./index";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import RadioGroup from "@mui/material/RadioGroup";
import Radio from "@mui/material/Radio";
import useDialog from "../../../components/useDialog";


export const snowBaseDataColumns = ['hs', 'hn', 'hnR', 'hnT', 'hnG', 'hnW', 'hnMm', 'hnMe', 'hnCum', 'hnStim', 'period', 'hnAux'];


const SnowBaseData = memo(({formik, warnings, inputDisabled, visibleColumns}) => {
    const {t} = useTranslation(['models1']);
    const hnTDialog = useDialog();

    const showCumAndStim = formik.values.period !== "H24"
    const hnDisabled = showCumAndStim || inputDisabled;

    const isVisible = (column) => (!visibleColumns || visibleColumns?.[column]);

    const handleChangeHnT = async (event) => {
        const checked = event.target.checked;
        if (checked) {
            hnTDialog.open();
        } else{
            await formik.setFieldValue('hnT', false);
            const hnField = showCumAndStim ? "hnCum" : "hn";
            await formik.setFieldValue(hnField, "");
        }
    }

    const hsNoValueWarning = formik.values.hs === "" || formik.values.hs === null || formik.values.hs === undefined;
    const hnNoValueWarning = formik.values.hn === "" || formik.values.hn === null || formik.values.hn === undefined;

    const hsWarnings = hsNoValueWarning ? [t('model1Form.hsNoValueWarning'), ...(warnings['hs'] ?? [])] : (warnings['hs']);
    const hnWarnings = hnNoValueWarning ? [t('model1Form.hsNoValueWarning'), ...(warnings['hn'] ?? [])] : (warnings['hn']);

    return <FormSection title={t('model1Form.snowBaseData')}>
        <Grid container rowSpacing={3} columnSpacing={4}>
            {
                isVisible("hs") &&
                <Grid item xs={12} md={6}>
                    <Model1MeasureField name="hs"
                                        formik={formik}
                                        unitOfMeasure="cm"
                                        warnings={{hs: hsWarnings}}
                                        disabled={inputDisabled} type="number"/>
                </Grid>
            }
            {
                isVisible("hn") &&
                <Grid item xs={12} md={6}>
                    <Model1MeasureField name="hn"
                                        formik={formik}
                                        unitOfMeasure="cm"
                                        warnings={{hn: hnWarnings}}
                                        disabled={hnDisabled} type="number"/>
                </Grid>
            }
            {
                isVisible("hnAux") &&
                <>
                    <Grid item xs={12} md={6}>
                        <FormGroup>
                            <FormControlLabel control={<Checkbox checked={formik.values.hnR}
                                                                 onChange={formik.handleChange}
                                                                 name="hnR"
                                                                 color="info"
                                                                 inputProps={{ 'aria-label': 'controlled' }}/>}
                                              label={t('model1Form.hnR')}
                                              disabled={inputDisabled}/>
                            <FormControlLabel control={<Checkbox checked={formik.values.hnT}
                                                                 onChange={handleChangeHnT}
                                                                 name="hnT"
                                                                 color="info"
                                                                 inputProps={{'aria-label': 'controlled'}}/>}
                                              label={t('model1Form.hnT')}
                                              disabled={inputDisabled}/>
                            <FormControlLabel control={<Checkbox checked={formik.values.hnG}
                                                                 onChange={formik.handleChange}
                                                                 name="hnG"
                                                                 color="info"
                                                                 inputProps={{'aria-label': 'controlled'}}/>}
                                              label={t('model1Form.hnG')}
                                              disabled={inputDisabled}/>
                        </FormGroup>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <FormGroup>
                            <FormControlLabel control={<Checkbox checked={formik.values.hnW}
                                                                 onChange={formik.handleChange}
                                                                 name="hnW"
                                                                 color="info"
                                                                 inputProps={{'aria-label': 'controlled'}}/>}
                                              label={t('model1Form.hnW')}
                                              disabled={inputDisabled}/>
                            <FormControlLabel control={<Checkbox checked={formik.values.hnMm}
                                                                 onChange={formik.handleChange}
                                                                 name="hnMm"
                                                                 color="info"
                                                                 inputProps={{'aria-label': 'controlled'}}/>}
                                              label={t('model1Form.hnMm')}
                                              disabled={inputDisabled}/>
                            <FormControlLabel control={<Checkbox checked={formik.values.hnMe}
                                                                 onChange={formik.handleChange}
                                                                 name="hnMe"
                                                                 color="info"
                                                                 inputProps={{'aria-label': 'controlled'}}/>}
                                              label={t('model1Form.hnMe')}
                                              disabled={inputDisabled}/>
                        </FormGroup>
                    </Grid>
                    {
                        showCumAndStim && <>
                            <Grid item xs={6}>
                                <Model1MeasureField name="hnCum" formik={formik} unitOfMeasure="cm" disabled={inputDisabled}/>
                            </Grid>
                            <Grid item xs={6}>
                                <Model1MeasureField name="hnStim" formik={formik} unitOfMeasure="cm" disabled={inputDisabled}/>
                            </Grid>
                        </>
                    }
                    {
                        hnTDialog.isOpen && <HnTDialog open={hnTDialog.isOpen}
                                                       onClose={hnTDialog.close}
                                                       showCumAndStim={showCumAndStim}
                                                       formik={formik}/>
                    }
                </>
            }
        </Grid>
    </FormSection>
}, (prevProps, nextProps) => formikPropertyEqual(prevProps, nextProps, snowBaseDataColumns));


const HnTDialog = ({open, onClose, showCumAndStim, formik}) => {
    const {t} = useTranslation(['models1']);

    const [hnValue, setHnValue] = useState((!isNaN(parseFloat(formik.values.hn)) && parseFloat(formik.values.hn) >= 1) ? "1" : "0");

    const handleChangeHnT = async () => {
        await formik.setFieldValue('hnT', true);
        const hnField = showCumAndStim ? "hnCum" : "hn";
        await formik.setFieldValue(hnField, hnValue);
        onClose();
    }

    return <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>{t('model1Form.hnTDialog.title')}</DialogTitle>
        <DialogContent>
            <DialogContentText>
                {t('model1Form.hnTDialog.description')}
            </DialogContentText>
            <FormControl>
                <RadioGroup value={hnValue} onChange={(event) => setHnValue(event.target.value)}>
                    <FormControlLabel value="0" control={<Radio />} label="0 cm <= HN < 0.5 cm" />
                    <FormControlLabel value="1" control={<Radio />} label="0.5 cm <= HN < 1 cm" />
                </RadioGroup>
            </FormControl>
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose}>{t('model1Form.hnTDialog.cancel')}</Button>
            <Button onClick={handleChangeHnT}>{t('model1Form.hnTDialog.confirm')}</Button>
        </DialogActions>
    </Dialog>
}

export default SnowBaseData;